<template>
  <div class="container catalog-root">
    <nav class="breadcrumb" aria-label="Ruta de navegación">
      <router-link to="/print3d">Impresión 3D</router-link>
      <span aria-hidden="true">›</span>
      <span aria-current="page">Catálogo completo</span>
    </nav>

    <header class="hero">
      <h1 class="title">Catálogo completo</h1>
      <p class="text hero-text">
        Todas las piezas que fabricamos, agrupadas por categoría. Busca por
        nombre o filtra por familia de producto; si no encuentras lo que
        necesitas, lo modelamos a tu medida.
      </p>
    </header>

    <section class="filters" aria-label="Filtros del catálogo">
      <label class="search">
        <span class="visually-hidden">Buscar producto por nombre</span>
        <input
          v-model="search"
          type="search"
          placeholder="Buscar por nombre o descripción…"
          autocomplete="off"
        />
      </label>

      <!-- Las categorías son píldoras y no un <select>: con pocos valores se
           leen de un vistazo y muestran cuántas piezas hay en cada una. -->
      <ul class="chips" role="list">
        <li>
          <button
            type="button"
            class="chip"
            :class="{ 'is-active': !activeCategory }"
            :aria-pressed="!activeCategory ? 'true' : 'false'"
            @click="selectCategory('')"
          >
            Todas <span class="count">{{ totalProducts }}</span>
          </button>
        </li>
        <li v-for="category in categories" :key="category.slug">
          <button
            type="button"
            class="chip"
            :class="{ 'is-active': activeCategory === category.slug }"
            :aria-pressed="activeCategory === category.slug ? 'true' : 'false'"
            @click="selectCategory(category.slug)"
          >
            {{ category.name }} <span class="count">{{ category.productCount }}</span>
          </button>
        </li>
      </ul>
    </section>

    <div v-if="loading" class="state-msg">Cargando catálogo…</div>
    <div v-else-if="error" class="state-msg state-error" role="alert">
      No fue posible cargar el catálogo. Intenta recargar la página.
    </div>

    <template v-else>
      <p class="results" role="status">
        {{ resultsLabel }}
      </p>

      <div v-if="products.length" class="grid">
        <ProductCard
          v-for="product in products"
          :key="product.id"
          :product="product"
          :material-name="materialNameFor(product.materialId)"
          @order-clicked="onOrderClicked"
        />
      </div>

      <div v-else class="state-msg empty">
        <p>No encontramos piezas con esos criterios.</p>
        <button type="button" class="reset-btn" @click="clearFilters">
          Limpiar filtros
        </button>
      </div>
    </template>

    <p v-if="orderConfirmation" class="ok-msg" role="status">{{ orderConfirmation }}</p>

    <section class="cta">
      <p class="text">
        ¿Buscas algo que no está en el catálogo? Cotiza tu propia pieza o pide
        que la modelemos desde cero.
      </p>
      <div class="cta-actions">
        <router-link class="cta-btn" to="/print3d">Cotizar una pieza</router-link>
        <WhatsAppButton
          variant="inline"
          label="Escríbenos por WhatsApp"
          message="Hola, tengo una consulta sobre el catálogo de impresión 3D."
        />
      </div>
    </section>

    <CatalogOrderModal
      v-if="showOrderModal && selectedProduct"
      ref="orderModal"
      :product="selectedProduct"
      @confirm="onConfirmOrder"
      @close="showOrderModal = false"
    />
  </div>
</template>

<script>
import ProductCard from './Print3D/ProductCard.vue';
import CatalogOrderModal from './Print3D/CatalogOrderModal.vue';
import WhatsAppButton from './WhatsAppButton.vue';
import {
  getProducts, getMaterials, getProductCategories, submitCatalogOrder,
} from '@/services/print3dService';

export default {
  name: 'Print3DCatalogPage',
  components: { ProductCard, CatalogOrderModal, WhatsAppButton },
  data() {
    return {
      products: [],
      materials: [],
      categories: [],
      // Total sin filtrar: alimenta el contador de la píldora "Todas".
      totalProducts: 0,
      // El estado de los filtros vive en la URL (?categoria=&q=) para que el
      // visitante pueda compartir o volver a una búsqueda con el historial.
      activeCategory: this.$route.query.categoria || '',
      search: this.$route.query.q || '',
      loading: true,
      error: null,
      selectedProduct: null,
      showOrderModal: false,
      orderConfirmation: '',
      debounceTimer: null,
    };
  },
  computed: {
    resultsLabel() {
      const total = this.products.length;
      const nombre = this.categories.find((c) => c.slug === this.activeCategory)?.name;
      const enCategoria = nombre ? ` en ${nombre}` : '';
      if (total === 1) return `1 producto${enCategoria}.`;
      return `${total} productos${enCategoria}.`;
    },
  },
  watch: {
    // La escritura filtra contra el backend; el debounce evita una petición
    // por pulsación.
    search() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.syncQuery();
        this.fetchProducts();
      }, 350);
    },
    // Navegación con atrás/adelante: la URL manda sobre el estado local.
    '$route.query': function (query) {
      const categoria = query.categoria || '';
      const q = query.q || '';
      if (categoria === this.activeCategory && q === this.search) return;
      this.activeCategory = categoria;
      this.search = q;
      this.fetchProducts();
    },
  },
  async mounted() {
    await this.fetchStatic();
    await this.fetchProducts();
  },
  beforeUnmount() {
    clearTimeout(this.debounceTimer);
  },
  methods: {
    async fetchStatic() {
      const [{ materials }, { categories }, { products }] = await Promise.all([
        getMaterials(),
        getProductCategories(),
        getProducts(),
      ]);
      this.materials = materials;
      this.categories = categories;
      this.totalProducts = products.length;
    },
    async fetchProducts() {
      this.loading = true;
      const { products, error } = await getProducts({
        category: this.activeCategory,
        search: this.search,
      });
      this.products = products;
      this.error = error;
      this.loading = false;
    },
    materialNameFor(id) {
      const found = this.materials.find((m) => m.id === id);
      return found ? found.displayName : 'Por definir';
    },
    selectCategory(slug) {
      this.activeCategory = this.activeCategory === slug ? '' : slug;
      this.syncQuery();
      this.fetchProducts();
    },
    clearFilters() {
      this.activeCategory = '';
      this.search = '';
      this.syncQuery();
      this.fetchProducts();
    },
    // `replace` y no `push`: los ajustes de filtro no deben llenar el historial
    // de entradas intermedias al escribir en el buscador.
    syncQuery() {
      const query = {};
      if (this.activeCategory) query.categoria = this.activeCategory;
      if (this.search) query.q = this.search;
      const actual = this.$route.query;
      if ((actual.categoria || '') === (query.categoria || '')
        && (actual.q || '') === (query.q || '')) return;
      this.$router.replace({ query });
    },
    onOrderClicked(product) {
      this.selectedProduct = product;
      this.orderConfirmation = '';
      this.showOrderModal = true;
    },
    async onConfirmOrder(orderPayload) {
      const { receipt, error } = await submitCatalogOrder(orderPayload);
      if (error) {
        this.$refs.orderModal?.setError(error);
        return;
      }
      this.orderConfirmation = `Pedido recibido (#${receipt.id}). Te contactaremos al correo ${orderPayload.contactEmail}.`;
      this.showOrderModal = false;
      this.selectedProduct = null;
    },
  },
};
</script>

<style scoped>
.catalog-root {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  padding-bottom: var(--space-xl);
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-family: var(--font-body);
  font-size: 0.9rem;
  color: var(--color-text);
}

.breadcrumb a {
  color: var(--color-action);
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

.hero .title {
  margin-bottom: 0.5rem;
}

.hero-text {
  max-width: 720px;
  margin: 0;
}

.filters {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.search input {
  width: 100%;
  max-width: 420px;
  box-sizing: border-box;
  padding: 0.55rem 0.9rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-family: var(--font-body);
  font-size: 1rem;
  color: var(--color-text-strong);
}

.search input:focus-visible {
  outline: 2px solid var(--color-action);
  outline-offset: 1px;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  list-style: none;
  margin: 0;
  padding: 0;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.8rem;
  border: 1px solid var(--color-border);
  border-radius: 999px;
  background: white;
  color: var(--color-text-strong);
  font-family: var(--font-body);
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
}

.chip:hover {
  border-color: var(--color-action);
}

.chip.is-active {
  background: var(--color-action);
  border-color: var(--color-action);
  color: white;
}

.chip .count {
  font-size: 0.78rem;
  opacity: 0.75;
}

.results {
  font-family: var(--font-body);
  font-size: 0.9rem;
  color: var(--color-text);
  margin: 0;
}

/* Rejilla fluida: la ficha conserva su ancho máximo de 300px y las columnas se
   recalculan solas, así el catálogo escala sin tocar el CSS al crecer. */
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1.25rem;
  justify-items: center;
  align-items: stretch;
}

.grid > :deep(.product-card) {
  width: 100%;
  max-width: 300px;
  height: 100%;
}

.state-msg {
  padding: 2rem;
  text-align: center;
  color: var(--color-text);
}

.state-error {
  color: #B91C1C;
}

.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
}

.empty p {
  margin: 0;
}

.reset-btn,
.cta-btn {
  display: inline-block;
  padding: 0.5rem 1.1rem;
  border: 1px solid var(--color-action);
  border-radius: 8px;
  background: white;
  color: var(--color-action);
  font-family: var(--font-body);
  font-size: 0.95rem;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.reset-btn:hover,
.cta-btn:hover {
  background: var(--color-action);
  color: white;
}

.ok-msg {
  background: rgba(22, 163, 74, 0.1);
  color: #166534;
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  margin: 0;
  font-size: 0.9rem;
}

.cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
  text-align: center;
  padding-top: var(--space-sm);
  border-top: 1px solid var(--color-border);
}

.cta .text {
  max-width: 620px;
  margin: 0;
}

.cta-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.6rem;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

@media (max-width: 800px) {
  .grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
  }
}
</style>
